import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Star, Briefcase, UserPlus, Zap, Mail, Phone, AlertCircle, Calendar, ChevronDown, ChevronRight, Trash2 } from 'lucide-react';
import type { BlastFundraiser, LinkedPerson, MunchFundCampaign } from '../../types';
import { MunchFundTeamSlice } from './MunchFundFundraisers';
import { effectiveFirstName, effectiveLastName, effectiveEmail, effectivePhone } from '../../lib/personPrecedence';
import { SourceIndicator } from './SourceIndicator';
import { BlastBadge } from './BlastBadge';
import { CrmOnlyBadge } from './CrmOnlyBadge';

const BLAST_APP_URL = import.meta.env.VITE_BLAST_APP_URL || 'https://dev.blastathletics.com';

interface TeamRowProps {
  team: {
    id: string;
    level: string;
    blastTeamId: string | null;
    isActive: boolean;
    lastActiveSchoolYear?: number | null;
    people: LinkedPerson[];
  };
  blastFundraisers?: BlastFundraiser[];
  munchFundCampaigns?: MunchFundCampaign[];
  onLinkPerson?: () => void;
  onScheduleActivity?: (personId: string, personName: string) => void;
  onConvert?: (personId: string, teamId: string) => void;
  /**
   * Gates the Add Person / Schedule / Convert affordances on this team row.
   * Defaults to true to preserve existing callsites. Parent SportCard also
   * already withholds the corresponding handlers in read-only mode, so this
   * is a defense-in-depth flag for clarity.
   */
  canEdit?: boolean;
  /** ADMIN-only: enables delete/unlink controls on this CRM-only team. */
  canDelete?: boolean;
  /** Opens the delete confirm for this team. Only provided to admins on CRM-only teams. */
  onRequestDeleteTeam?: () => void;
  /** Opens the unlink confirm for a rep-added coach on this team. */
  onRequestUnlinkCoach?: (link: LinkedPerson) => void;
}

function formatCompactCurrency(amount: number): string {
  if (amount >= 1000) return `$${Math.round(amount / 1000)}K`;
  return `$${amount}`;
}

/**
 * Row within a sport card showing a team level and its linked people
 */
export const TeamRow = ({ team, blastFundraisers = [], munchFundCampaigns = [], onLinkPerson, onScheduleActivity, onConvert, canEdit = true, canDelete = false, onRequestDeleteTeam, onRequestUnlinkCoach }: TeamRowProps) => {
  const blastCount = blastFundraisers.length;
  const blastTotalDollars = blastFundraisers.reduce(
    (sum, f) => sum + (f.totalRaisedCents ? Number(BigInt(f.totalRaisedCents)) / 100 : 0),
    0,
  );

  const headerBgClasses = team.blastTeamId
    ? 'bg-green-50/40 dark:bg-green-900/10'
    : 'bg-gray-50 dark:bg-gray-800/50';

  // CRM-33: payload includes former (end-dated) links; show current by default,
  // reveal this team's former coaches on demand (client-side only — no refetch).
  const [showFormerCoaches, setShowFormerCoaches] = useState(false);
  const currentPeople = team.people.filter((l) => l.endDate == null);
  const formerPeople = team.people.filter((l) => l.endDate != null);

  const renderPerson = (link: LinkedPerson, isFormer: boolean) => {
    const phone = effectivePhone(link.person);
    const email = effectiveEmail(link.person);
    const firstName = effectiveFirstName(link.person);
    const lastName = effectiveLastName(link.person);
    const formerYear = isFormer && link.endDate ? new Date(link.endDate).getUTCFullYear() : null;
    return (
      <Link
        key={link.id}
        to={`/people/${link.person.id}`}
        className="flex items-start px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-colors"
      >
        <div className="flex-shrink-0 h-7 w-7 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mt-0.5">
          <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
            {firstName[0]}{lastName[0]}
          </span>
        </div>
        <div className="ml-2.5 flex-1 min-w-0">
          {/* Name row */}
          <div className="flex items-center gap-2">
            <SourceIndicator source={link.person.blastUserId ? 'BLAST' : 'CRM'} />
            <span className="text-sm text-blast-600 dark:text-blast-400 hover:underline truncate">
              {firstName} {lastName}
            </span>
            {link.role && (
              <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center flex-shrink-0">
                <Briefcase className="h-3 w-3 mr-0.5" />
                {link.role}
              </span>
            )}
            {formerYear && (
              <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">
                · last {formerYear}
              </span>
            )}
            {link.isPrimary && (
              <Star className="h-3 w-3 text-amber-500 fill-current flex-shrink-0" />
            )}
            {canEdit && onScheduleActivity && (
              <button
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onScheduleActivity(link.person.id, `${firstName} ${lastName}`); }}
                className="p-0.5 text-gray-400 hover:text-blast-600 dark:hover:text-blast-400 transition-colors flex-shrink-0"
                title="Schedule activity"
              >
                <Calendar className="h-3.5 w-3.5" />
              </button>
            )}
            {canEdit && onConvert && !link.person.blastUserId && !link.person.convertedAt && (
              <button
                data-testid="convert-button-team-row"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onConvert(link.person.id, team.id); }}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 border border-amber-200 dark:border-amber-800/50 hover:bg-amber-200 dark:hover:bg-amber-900/50 transition-colors flex-shrink-0"
                title="Convert to Blast"
              >
                <Zap className="h-3 w-3" />
                Convert
              </button>
            )}
            {canDelete && onRequestUnlinkCoach && !isFormer && !link.managedBySync && (
              <button
                type="button"
                aria-label={`Unlink ${firstName} ${lastName}`}
                title="Unlink from this team"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRequestUnlinkCoach(link); }}
                className="p-0.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors flex-shrink-0"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          {/* Contact info row — stacks on mobile so neither email nor phone gets truncated. */}
          <div className="flex flex-col gap-0.5 sm:flex-row sm:items-center sm:gap-3 mt-0.5 text-xs">
            {email ? (
              <span className="text-gray-500 dark:text-gray-400 flex items-center min-w-0">
                <Mail className="h-3 w-3 mr-1 flex-shrink-0" />
                <span className="sm:truncate">{email}</span>
              </span>
            ) : (
              <span className="text-red-400 dark:text-red-500 flex items-center">
                <AlertCircle className="h-3 w-3 mr-0.5" />
                No email
              </span>
            )}
            {phone ? (
              <span className="text-gray-500 dark:text-gray-400 flex items-center flex-shrink-0">
                <Phone className="h-3 w-3 mr-1" />
                {phone}
              </span>
            ) : (
              <span className="text-red-400 dark:text-red-500 flex items-center flex-shrink-0">
                <AlertCircle className="h-3 w-3 mr-0.5" />
                No phone
              </span>
            )}
          </div>
        </div>
      </Link>
    );
  };

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      {/* Team header — mobile-first stacking. */}
      <div className={`flex flex-col gap-2 md:flex-row md:items-center md:justify-between px-4 py-2.5 ${headerBgClasses}`}>
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {team.level}
          </span>
          {!team.isActive && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
              Former{team.lastActiveSchoolYear ? ` · last ${team.lastActiveSchoolYear}` : ''}
            </span>
          )}
          {team.blastTeamId ? (
            <BlastBadge label="Blast" href={`${BLAST_APP_URL}/teams/${team.blastTeamId}`} />
          ) : (
            <CrmOnlyBadge title="This team is not linked to Blast yet." />
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap md:flex-shrink-0">
          {blastCount > 0 && (
            <span className="text-xs text-green-600 dark:text-green-400" title="Current school year (Jul 1 → Jun 30)">
              Blast this SY: {blastCount} · {formatCompactCurrency(blastTotalDollars)}
            </span>
          )}
          <MunchFundTeamSlice campaigns={munchFundCampaigns} />
          <span className="text-xs text-gray-400 dark:text-gray-500">
            {currentPeople.length} {currentPeople.length === 1 ? 'person' : 'people'}
          </span>
          {canEdit && onLinkPerson && (
            <button
              onClick={onLinkPerson}
              className="p-1 text-gray-400 hover:text-blast-600 dark:hover:text-blast-400 transition-colors"
              title="Add person to this team"
            >
              <UserPlus className="h-3.5 w-3.5" />
            </button>
          )}
          {canDelete && onRequestDeleteTeam && team.blastTeamId == null && (
            <button
              type="button"
              aria-label={`Delete team ${team.level}`}
              title={currentPeople.length > 0 ? 'Remove this team\'s coaches first' : 'Delete this team'}
              disabled={currentPeople.length > 0}
              onClick={onRequestDeleteTeam}
              className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400 disabled:opacity-40 disabled:hover:text-gray-400 transition-colors"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* People list (current) */}
      {currentPeople.length > 0 ? (
        <div className="divide-y divide-gray-100 dark:divide-gray-700/50">
          {currentPeople.map((link) => renderPerson(link, false))}
        </div>
      ) : formerPeople.length === 0 ? (
        <div className="px-4 py-3 text-center">
          <p className="text-xs text-gray-400 dark:text-gray-500">No people linked to this team</p>
        </div>
      ) : null}

      {/* Former coaches — revealed on demand (CRM-33), client-side only */}
      {formerPeople.length > 0 && (
        <div className="border-t border-gray-100 dark:border-gray-700/50">
          <button
            type="button"
            onClick={() => setShowFormerCoaches((v) => !v)}
            aria-expanded={showFormerCoaches}
            className="w-full flex items-center gap-1 px-4 py-2 text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            {showFormerCoaches ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            {showFormerCoaches ? 'Hide' : 'Show'} {formerPeople.length} former
          </button>
          {showFormerCoaches && (
            <div className="divide-y divide-gray-100 dark:divide-gray-700/50 opacity-75">
              {formerPeople.map((link) => renderPerson(link, true))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
